// src/components/ambassadors/RegistrationForm.tsx
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { ambassadorApplicationService } from "../../services/ambassador-application.service";
import { AmbassadorApplicationData } from "../../types/ambassador.types";

interface RegistrationFormProps {
  onSuccess: () => void;
}

const initialForm: AmbassadorApplicationData = {
  fullName: "",
  email: "",
  whatsappNumber: "",
  country: "",
  city: "",
  ageRange: "",
  instagramHandle: "",
  tiktokHandle: "",
  strongestPlatform: "instagram",
  followerRange: "",
  contentNiche: "",
  audienceSummary: "",
  bestContentLink1: "",
  bestContentLink2: "",
  whyJoin: "",
  howWouldYouShareLrnsy: "",
  availableForZoom: false,
  comfortableWithKpis: false,
  preferredPayoutMethod: "",
};

export function RegistrationForm({ onSuccess }: RegistrationFormProps) {
  const { user, isAuthenticated, signInWithGoogle } = useAuth();
  const [formData, setFormData] = useState<AmbassadorApplicationData>({
    ...initialForm,
    fullName: user?.name || "",
    email: user?.email || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = <K extends keyof AmbassadorApplicationData>(
    key: K,
    value: AmbassadorApplicationData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated) {
      const ok = await signInWithGoogle();
      if (!ok) return;
      toast("Please complete and submit your application after signing in.");
      return;
    }

    if (!formData.availableForZoom || !formData.comfortableWithKpis) {
      toast.error("Please confirm Zoom availability and readiness.");
      return;
    }

    setIsSubmitting(true);
    try {
      await ambassadorApplicationService.submitApplication(user?.id || null, {
        ...formData,
        fullName: formData.fullName || user?.name || "",
        email: formData.email || user?.email || "",
      });

      setFormData({
        ...initialForm,
        fullName: user?.name || "",
        email: user?.email || "",
      });

      onSuccess();
    } catch (error: any) {
      toast.error(error?.message || "We couldn’t submit your application.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="max-w-3xl mx-auto"
    >
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white mb-2 text-center">
          Apply to Become a{" "}
          <span
            className="text-transparent"
            style={{ WebkitTextStroke: "1px rgba(255,255,255,0.8)" }}
          >
            LRNSY Ambassador
          </span>
        </h2>
        <p className="text-center text-white/45 text-sm mb-10 max-w-2xl mx-auto">
          We review every application carefully. Shortlisted creators will be invited to a short
          Zoom conversation before onboarding.
        </p>

        <form onSubmit={handleSubmit} className="space-y-10">
          <section className="space-y-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 mb-2">
                Section 1
              </p>
              <h3 className="text-xl font-black text-white">About You</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => update("fullName", e.target.value)}
                placeholder="Full name"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="Email address"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
              <input
                type="text"
                required
                value={formData.whatsappNumber}
                onChange={(e) => update("whatsappNumber", e.target.value)}
                placeholder="WhatsApp number"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="City"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
              <select
                required
                value={formData.country}
                onChange={(e) => update("country", e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              >
                <option value="">Select country</option>
                <option value="Kenya">Kenya</option>
                <option value="Uganda">Uganda</option>
                <option value="Tanzania">Tanzania</option>
                <option value="Rwanda">Rwanda</option>
                <option value="Nigeria">Nigeria</option>
                <option value="Ghana">Ghana</option>
                <option value="South Africa">South Africa</option>
                <option value="Other">Other</option>
              </select>
              <select
                required
                value={formData.ageRange}
                onChange={(e) => update("ageRange", e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              >
                <option value="">Age range</option>
                <option value="18-20">18–20</option>
                <option value="21-25">21–25</option>
                <option value="26-30">26–30</option>
                <option value="31+">31+</option>
              </select>
            </div>
          </section>

          <section className="space-y-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 mb-2">
                Section 2
              </p>
              <h3 className="text-xl font-black text-white">Platforms & Audience</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <input
                type="text"
                value={formData.instagramHandle}
                onChange={(e) => update("instagramHandle", e.target.value)}
                placeholder="Instagram handle"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
              <input
                type="text"
                value={formData.tiktokHandle}
                onChange={(e) => update("tiktokHandle", e.target.value)}
                placeholder="TikTok handle"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
              <select
                required
                value={formData.strongestPlatform}
                onChange={(e) =>
                  update("strongestPlatform", e.target.value as "instagram" | "tiktok" | "both")
                }
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              >
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="both">Both</option>
              </select>
              <select
                required
                value={formData.followerRange}
                onChange={(e) => update("followerRange", e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              >
                <option value="">Follower range</option>
                <option value="2k-5k">2k–5k</option>
                <option value="5k-10k">5k–10k</option>
                <option value="10k-20k">10k–20k</option>
                <option value="20k+">20k+</option>
              </select>
            </div>

            <input
              type="text"
              required
              value={formData.contentNiche}
              onChange={(e) => update("contentNiche", e.target.value)}
              placeholder="Content niche (e.g. lifestyle, relationships, events, storytelling)"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
            />

            <textarea
              required
              value={formData.audienceSummary}
              onChange={(e) => update("audienceSummary", e.target.value)}
              placeholder="Describe your audience briefly."
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white resize-none"
            />

            <div className="grid md:grid-cols-2 gap-5">
              <input
                type="url"
                required
                value={formData.bestContentLink1}
                onChange={(e) => update("bestContentLink1", e.target.value)}
                placeholder="Best content link 1"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
              <input
                type="url"
                required
                value={formData.bestContentLink2}
                onChange={(e) => update("bestContentLink2", e.target.value)}
                placeholder="Best content link 2"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
            </div>
          </section>

          <section className="space-y-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 mb-2">
                Section 3
              </p>
              <h3 className="text-xl font-black text-white">Fit & Readiness</h3>
            </div>

            <textarea
              required
              value={formData.whyJoin}
              onChange={(e) => update("whyJoin", e.target.value)}
              placeholder="Why do you want to become a LRNSY ambassador?"
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white resize-none"
            />

            <textarea
              required
              value={formData.howWouldYouShareLrnsy}
              onChange={(e) => update("howWouldYouShareLrnsy", e.target.value)}
              placeholder="How would you naturally introduce LRNSY to your audience?"
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white resize-none"
            />

            <select
              required
              value={formData.preferredPayoutMethod}
              onChange={(e) => update("preferredPayoutMethod", e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
            >
              <option value="">Preferred payout method</option>
              <option value="M-PESA">M-PESA</option>
              <option value="Bank">Bank</option>
              <option value="PayPal">PayPal</option>
              <option value="Other">Other</option>
            </select>

            <label className="flex items-start gap-3 text-sm text-white/75">
              <input
                type="checkbox"
                checked={formData.availableForZoom}
                onChange={(e) => update("availableForZoom", e.target.checked)}
                className="mt-1"
              />
              I’m available for virtual meetings if shortlisted.
            </label>

            <label className="flex items-start gap-3 text-sm text-white/75">
              <input
                type="checkbox"
                checked={formData.comfortableWithKpis}
                onChange={(e) => update("comfortableWithKpis", e.target.checked)}
                className="mt-1"
              />
              I’m comfortable working with clear expectations, content direction, and performance
              goals.
            </label>
          </section>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full group relative overflow-hidden px-8 py-4 bg-gradient-to-r from-amber-500 to-rose-500 text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-xl hover:shadow-amber-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {isSubmitting ? (
                <>Submitting Application...</>
              ) : (
                <>
                  Submit My Application
                  <ArrowRight
                    size={14}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </>
              )}
            </span>
          </button>
        </form>

        <p className="text-center text-[11px] text-white/30 mt-6">
          Shortlisted applicants will be contacted for the next step.
        </p>
      </div>
    </motion.div>
  );
}
